import { matchPath } from 'react-router-dom';
import { FULLCARE_MENU_DESTINATIONS, FULLCARE_DEEP_LINK_DESTINATIONS, FULLCARE_CANDIDATE_DESTINATIONS } from '../navigation-v2/registry/fullCareRegistry';
import { PRODUCT_MENU_FEATURES } from './productScope';
import { PRODUCT_EXTRA_ROUTES, PRODUCT_DISPATCH_ROUTES } from './productRouteCoverage';

export const PRODUCT_SECTIONS = Object.freeze({
  individual: {
    notes: ['logbook.entries', 'workspace.animals'],
    documents: ['workspace.animals', 'logbook.entries'],
    followUp: ['welfare.goals'],
    training: ['monitoring.training'],
    health: ['monitoring.individualParameters', 'logbook.treatments'], welfare: ['assessments.individual', 'assessments.species', 'observations.focal', 'observations.scan'],
    'health.params': ['monitoring.individualParameters'], 'health.bloodPanels': ['monitoring.individualParameters'], 'health.notes': ['logbook.treatments'], 'health.prescriptions': ['logbook.treatments'],
    'welfare.fasteval': ['assessments.individual'], 'welfare.actions': ['welfare.goals'],
    'welfare.questionnaire': ['assessments.species'], 'welfare.observations': ['observations.focal'], 'welfare.scan': ['observations.scan'],
    'welfare.social': ['observations.focal', 'observations.focalAnalysis'],
    prescriptions: ['logbook.treatments'], healthNotes: ['logbook.treatments'],
  },
  group: { animals: ['workspace.animals'], history: ['workspace.animals'], notes: ['logbook.entries', 'workspace.animals'], documents: ['workspace.animals', 'logbook.entries'], customParameters: ['monitoring.individualParameters'], evaluation: ['assessments.rapid'], evaluations: ['assessments.rapid'], welfare: ['assessments.rapid'], actions: ['welfare.goals'], followUp: ['welfare.goals'], prescriptions: ['logbook.treatments'] },
  enclosure: {
    followUp: ['welfare.goals'],
    notes: ['logbook.entries'],
    tools: ['logbook.entries'],
    documents: ['workspace.enclosures', 'workspace.animals', 'logbook.entries'],
    tasks: ['logbook.tasks'], enrichment: ['monitoring.enrichment'],
    // The environment tab also manages scan areas, needed by Essential.
    environment: ['observations.scan', 'monitoring.environmentalParameters'],
    'tools.informations': ['logbook.entries'],
    'tools.documents': ['workspace.enclosures', 'workspace.animals', 'logbook.entries'],
    'tools.customParameters': ['monitoring.individualParameters'],
    'tools.treatments': ['logbook.treatments'], 'tools.automations': ['logbook.recurringTasks'], 'tools.weatherProtocols': ['extensions.weather'],
    'tools.environmentalParameters': ['monitoring.environmentalParameters'],
    'tools.training': ['monitoring.training'], 'tools.sensors': ['monitoring.environmentalParameters'],
  },
  sector: {
    followUp: ['welfare.goals'],
    notes: ['logbook.entries'],
    report: ['logbook.entries'], tasks: ['logbook.tasks'],
    // Installation rapide des enrichissements, rubrique propre à la V2.
    enrichment: ['monitoring.enrichment'],
    tools: ['logbook.entries'],
    documents: ['logbook.entries'],
    'tools.documents': ['logbook.entries'],
    'tools.informations': ['logbook.entries'],
    'tools.treatments': ['logbook.treatments'], 'tools.automations': ['logbook.recurringTasks'],
    'tools.weatherProtocols': ['extensions.weather'], 'tools.customParameters': ['monitoring.environmentalParameters'],
    'main.report': ['logbook.templates'],
  },
  establishment: { apiTokens: ['integrations.api'], zims: ['zims.import', 'zims.syncIn', 'zims.syncOut'], weather: ['extensions.weather'] },
});

export const canUseProductSection = (scope, entity, section, subsection) => {
  const policy = PRODUCT_SECTIONS[entity] || {};
  // Enclosure tools is a container: any accessible child makes it useful.
  // The selected child still has its own gate (including direct links).
  const parentAllowed = entity === 'enclosure' && section === 'tools'
    ? Object.entries(policy).some(([key, features]) => key.startsWith('tools.') && scope.allows(features))
    : scope.allows(policy[section]);
  return parentAllowed && (!subsection || scope.allows(policy[`${section}.${subsection}`]));
};

export const PRODUCT_VETERINARY_FEATURES = Object.freeze({ clinical: 'logbook.treatments', pharmacy: 'logbook.pharmacy', library: 'logbook.treatments', rafiki: 'logbook.assistant', tasks: 'logbook.tasks' });
const overrides = {
  'health.veterinary-dashboard': ['logbook.treatments', 'logbook.pharmacy', 'logbook.assistant'],
  'collection.social': ['observations.focal', 'observations.focalAnalysis'],
  'collection.gallery': ['workspace.animals', 'workspace.groups', 'workspace.enclosures'],
  'collection.enclosure-sensors': ['monitoring.environmentalParameters'],
  'health.veterinary-clinical': ['logbook.treatments'], 'health.veterinary-library': ['logbook.treatments'],
  'health.veterinary-pharmacy': ['logbook.pharmacy'], 'health.veterinary-rafiki': ['logbook.assistant'],
  'welfare.fast-evaluation-results': ['assessments.rapid', 'assessments.individual'],
  'welfare.fast-evaluation-gallery': ['assessments.rapid', 'assessments.individual'],
  'welfare.fast-evaluation-comparison': ['assessments.reports'],
  'welfare.observation-result': ['observations.focal'],
};
const destinations = [...FULLCARE_MENU_DESTINATIONS, ...FULLCARE_DEEP_LINK_DESTINATIONS, ...FULLCARE_CANDIDATE_DESTINATIONS];
const specificity = (pattern) => pattern.split('/').reduce((score, part) => score + (part ? part.startsWith(':') ? 1 : 100 : 0), 0);
const matches = (pathname, pattern, prefix = false) => matchPath(pathname, { path: pattern, exact: !prefix });

/** Explicit classification is also consumed by the independent router-inventory check. */
export function classifyProductRoute(location = {}) {
  const pathname = location.pathname || '/';
  const query = new URLSearchParams(location.search || '');
  const state = location.state || {};
  const targetType = query.get('targetType') || state.targetType;
  if (/^\/v2\/campaigns\/planning(?:\/[^/]+)?\/?$/i.test(pathname)) {
    return { kind: 'product', requirements: ['observations.campaigns'], reason: 'Planning list and published consultation; generation has its own Progress capability' };
  }
  if (/^\/(v2\/welfare\/monitoring|CampaignQuizzMonitoringView)\/?$/i.test(pathname)) {
    const tab = query.get('tab') || state.activeTab;
    const feature = { observation: 'observations.focal', obs: 'observations.focal', questionnaire: 'assessments.species', quiz: 'assessments.species', quizz: 'assessments.species', scan: 'observations.scan' }[tab];
    return { kind: 'product', requirements: feature ? [feature] : ['assessments.species', 'observations.focal', 'observations.scan'], reason: 'Monitoring hub with independently filtered collection types' };
  }
  if (/^\/(v2\/daily-reports|DailyReportsView)\/?$/i.test(pathname)) {
    const section = query.get('section') || state.section;
    return { kind: 'product', requirements: section === 'templates' ? ['logbook.templates'] : section === 'assistant' ? ['logbook.assistant'] : ['current', 'history'].includes(section) ? ['logbook.dailyReports'] : ['logbook.dailyReports', 'logbook.templates', 'logbook.assistant'], reason: 'Daily report hub with independent templates and assistant' };
  }
  if (/^\/(v2\/tasks|TaskManagementView)\/?$/i.test(pathname)) {
    const view = query.get('view') || state.viewMode;
    const requirements = view === 'automations' ? ['logbook.recurringTasks']
      : query.get('taskId') || state.taskId || view === 'tasks' ? ['logbook.tasks']
      : ['logbook.tasks', 'logbook.recurringTasks'];
    return { kind: 'product', requirements, reason: 'Task hub and independent recurring task workspace' };
  }
  if (/^\/(v2\/fast-evaluations(?:\/evaluate)?|FastEvaluationView|FastEvalGroupListView)\/?$/i.test(pathname)
    && (targetType === 'individual' || query.get('view') === 'individuals')) {
    return { kind: 'product', requirements: ['assessments.individual'], reason: 'Individual evaluation selected in route context' };
  }
  if (/^\/(v2\/fast-evaluations(?:\/evaluate)?|FastEvaluationView|FastEvalGroupListView)\/?$/i.test(pathname)) {
    return { kind: 'product', requirements: ['assessments.rapid', 'assessments.species', 'assessments.customGrids', 'assessments.individual'], reason: 'Evaluation workspace with independently filtered grid types' };
  }
  const candidates = destinations.flatMap((destination) => ['v1', 'v2'].flatMap((version) => {
    const pattern = destination.versions?.[version]?.pattern;
    return pattern && matches(pathname, pattern) ? [{ pattern, kind: productDestinationRequirements(destination).length ? 'product' : 'shared', requirements: productDestinationRequirements(destination), destinationId: destination.id }] : [];
  }));
  PRODUCT_EXTRA_ROUTES.forEach((entry) => entry.patterns.forEach((pattern) => {
    if (matches(pathname, pattern)) candidates.push({ ...entry, pattern });
  }));
  candidates.sort((a, b) => specificity(b.pattern) - specificity(a.pattern));
  if (candidates.length) return candidates[0];
  const dispatcher = PRODUCT_DISPATCH_ROUTES.find(({ pattern, prefix }) => matches(pathname, pattern, prefix));
  if (dispatcher) return { kind: dispatcher.fallback === null ? 'unknown' : 'dispatcher', requirements: dispatcher.fallback, pattern: dispatcher.pattern, reason: dispatcher.reason || 'Classified family with independently filtered concrete variants' };
  return { kind: 'unknown', requirements: null };
}
export const productRouteRequirements = (location) => classifyProductRoute(location).requirements;
// Aucune route du routeur ne correspond (la couverture des routes garantit que
// toute route déclarée est classée) : adresse inexistante, pas fonction hors
// offre. Les frontières renvoient alors au tableau de bord (recette 12.1.0,
// ROB-50) au lieu d'afficher « Fonction non activée pour ce parc ».
export const isUnroutedLocation = (location) => {
  const route = classifyProductRoute(location);
  return route.kind === 'unknown' && !route.pattern;
};

// Recette 12.1.0 (EVA-34) : un lien direct vers une fonction d'évaluation rapide hors
// offre (Discovery : individus, comparaison, grilles) nomme la fonction retirée, en
// Nouvelle comme en Classique, au lieu de « Fonction non activée pour ce parc ».
const INDIVIDUAL_UNAVAILABLE = Object.freeze({ titleKey: 'fastEvaluationsV2.individualsTab', descriptionKey: 'fastEvaluationsV2.individualDisabled' });
const GRIDS_UNAVAILABLE = Object.freeze({ titleKey: 'resourcesV2.states.forbiddenTitle', descriptionKey: 'productScope.routes.gridAuthoring' });
const COMPARISON_UNAVAILABLE = Object.freeze({ titleKey: 'navigationV2.destinations.welfare.fast-evaluation-comparison.label', descriptionKey: 'productScope.routes.fastEvaluationComparison' });
const requiresOnly = (route, feature) => route.requirements?.length === 1 && route.requirements[0] === feature;
/** Clés i18n { titleKey, descriptionKey } du refus propre à la route, ou null (message générique). */
export function productUnavailableMessage(location = {}) {
  const route = classifyProductRoute(location);
  if (route.destinationId === 'welfare.fast-evaluation-comparison') return COMPARISON_UNAVAILABLE;
  if (requiresOnly(route, 'assessments.individual')) return INDIVIDUAL_UNAVAILABLE;
  if (requiresOnly(route, 'assessments.gridAuthoring')) return GRIDS_UNAVAILABLE;
  return null;
}

// A park without composition keeps the router's own fallback for unclassified URLs; the
// resolver already lets an explicit Rafiki setting withhold only the Rafiki capabilities.
const isLegacyScope = (scope) => scope?.mode === 'legacy';
const blockedInLegacy = (scope, requirements) => requirements?.length > 0 && !scope.allows(requirements);

export function canOpenProductLocation(scope, location = {}) {
  const requirements = productRouteRequirements(location);
  if (isLegacyScope(scope)) return !blockedInLegacy(scope, requirements);
  if (requirements === null || !scope.allows(requirements)) return false;
  const path = location.pathname || '/';
  // PlanningEntry keeps its draft mounted and renders an inert unavailable state
  // when generation is withdrawn. The campaign capability still gates this route.
  const query = new URLSearchParams(location.search || '');
  const state = location.state || {};
  const section = query.get('section') || query.get('activeTab') || state.section || state.activeTab;
  const entity = /^\/(v2\/individuals\/[^/]+|AnimalManagementView)$/i.test(path) ? 'individual'
    : /^\/(v2\/groups\/[^/]+|AdvancedGroupManagementView)$/i.test(path) ? 'group'
    : /^\/(v2\/enclosures\/[^/]+|EnclosureManagementView)$/i.test(path) ? 'enclosure'
    : /^\/(v2\/sectors\/[^/]+|SectorManagementView)$/i.test(path) ? 'sector'
    : /^\/(v2\/establishment|ZooDetails)$/i.test(path) ? 'establishment' : null;
  const subsection = entity === 'individual'
    ? section === 'health' ? query.get('healthSubTab') || state.healthSubTab
      : section === 'welfare' ? query.get('welfareSubTab') || state.welfareSubTab : null
    : query.get('subsection') || query.get('toolSection') || state.subsection;
  // Establishment authorizes and loads the requested park before evaluating its own scope.
  return !entity || entity === 'establishment' || canUseProductSection(scope, entity, section, subsection);
}

export const productDestinationRequirements = (destination) => overrides[destination.id] || PRODUCT_MENU_FEATURES[destination.accessKey] || [];
export const productDestinationKey = (node) => `product:${node.id}`;
// Without composition the registry stays identical; only a destination withheld by the
// explicit Rafiki setting is re-keyed to a product key that nobody grants.
const rekey = (node) => ({ ...node, accessKey: productDestinationKey(node), accessKeysAnyOf: undefined });
export function productNavigationRegistry(nodes, scope) {
  if (isLegacyScope(scope)) {
    const blocked = nodes.filter((node) => node.accessKey && blockedInLegacy(scope, productDestinationRequirements(node)));
    return blocked.length ? nodes.map((node) => blocked.includes(node) ? rekey(node) : node) : nodes;
  }
  return nodes.map((node) => node.accessKey && productDestinationRequirements(node).length ? rekey(node) : node);
}
export function grantProductDestinationKeys(keys, nodes, scope) {
  if (isLegacyScope(scope)) return keys;
  nodes.forEach((node) => {
    const requirements = productDestinationRequirements(node);
    if (node.accessKey && requirements.length && scope.allows(requirements)
      && (keys.has(node.accessKey) || node.accessKeysAnyOf?.some((key) => keys.has(key)))) keys.add(productDestinationKey(node));
  });
  return keys;
}

export function canOpenProductHref(scope, href) {
  if (!href) return false;
  const location = new URL(href, "http://product.local");
  return canOpenProductLocation(scope, location);
}

/** Only an unspecified veterinary root chooses the first included workspace. */
export function resolveVeterinaryProductLocation(scope, location = {}) {
  if (!scope || isLegacyScope(scope) || !/^\/(?:v2\/veterinary|VeterinaryDashboardView)\/?$/i.test(location.pathname || '')) return location;
  const first = ['clinical', 'pharmacy', 'library', 'rafiki'].find((area) => scope.can(PRODUCT_VETERINARY_FEATURES[area]));
  return first ? { ...location, pathname: `${location.pathname.replace(/\/$/, '')}/${first}` } : location;
}
